import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { CompanyDTO } from '@app/shared/services/dtos/company/company.dto';
import { CompaniesHttpService } from '@app/shared/services/http/companies-http.service';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { Company } from '../types/models/company/company.model';
import { CooperationUser } from '../types/models/cooperation/cooperation-user.model';
import { AppEvent } from '../types/models/notifications/app-event';
import { User } from '../types/models/user/user.model';
import { AuthService } from './auth.service';
import { DataService } from './data-service';
import { CompanyFactory } from './factories/company.factory';

/**
 * CompaniesDataService - Stores companies and the signed-in user's company id lists.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: {@link ActiveCompanyService}, {@link DirectUserCompanyGuard}, layout company switcher,
 * and features that resolve a company by id.
 *
 * Architecture:
 * - {@link CompaniesDataService}: Store and HTTP for companies
 * - {@link ActiveCompanyService}: Active company context for the shell
 * - {@link DirectUserCompanyGuard}: Waits for this store before allowing company admin routes
 */
@Injectable({ providedIn: 'root' })
export class CompaniesDataService extends DataService<CompanyDTO, Company> {
  private readonly companies: DataStore<Company> = new DataStore<Company>();
  private readonly userCompanies: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly companiesForCooperation: WritableSignal<Company[] | undefined> = signal(undefined);
  private readonly userCompaniesLoadInFlight = new Set<string>();

  private readonly authorizationService = inject(AuthService);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly companiesHttpService: CompaniesHttpService = inject(CompaniesHttpService);
  private readonly companyFactory: CompanyFactory = inject(CompanyFactory);
  private readonly authService: AuthService = inject(AuthService);

  public constructor() {
    super();
    effect(() => {
      const userId = this.authService.currentUser()?.id;
      if (!userId) {
        return;
      }
      this.loadUserCompanies(userId);
    });
  }
  public getUserCompanies(userId: string): Signal<Company[] | undefined> {
    if (!userId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.userCompanies
          .get(userId)()
          ?.ids?.map(id => this.companies.get(id)()) || []
    );
  }

  public getById(companyId: string): Signal<Company | undefined> {
    return computed(() => this.companies.get(companyId)());
  }

  /**
   * Company-id collection for the user. The store entry stays undefined until the list HTTP call finishes.
   * Callers must read this signal (not only {@link DataStore.hasDataForId}) so they re-run after upsert.
   */
  public getUserCompanyCollection(userId: string): Signal<IdsCollection | undefined> {
    return this.userCompanies.get(userId) ?? signal(undefined);
  }

  public loadCompaniesForCooperation(companyId: string): void {
    this.companiesHttpService.getCompaniesForCooperation(companyId).subscribe({
      next: (dtos: CompanyDTO[]) => {
        this.companiesForCooperation.set(dtos.map(dto => this.companyFactory.produce(dto)));
      },
      error: () => {
        this.companiesForCooperation.set([]);
      }
    });
  }

  public getCompaniesForCooperation(): Signal<Company[]> {
    return computed(() => this.companiesForCooperation() ?? []);
  }

  protected handleEvent(event: AppEvent): void {
    if (event.eventType == 'UserSignedIn') {
      this.loadUserCompanies((event.data as User).id);
    }

    if (
      event.eventType == 'CooperationAccepted' ||
      event.eventType == 'CooperationRejected' ||
      event.eventType == 'CooperationDeactivated'
    ) {
      const user = this.authorizationService.currentUser();
      if (user) {
        this.loadUserCompanies(user.id);
      }
    }

    if (event.eventType == 'CooperationUserAdded' || event.eventType == 'CooperationUserRemoved') {
      const currentUser = this.authorizationService.currentUser();
      if (currentUser) {
        const cooperationUser = event.data as CooperationUser;
        if (cooperationUser.user()?.id == currentUser.id) {
          this.fetchUserCompanies(currentUser.id);
        }
      }
    }
  }

  public loadUserCompanies(userId: string): void {
    if (!userId || this.userCompanies.hasDataForId(userId) || this.userCompaniesLoadInFlight.has(userId)) {
      return;
    }
    this.userCompaniesLoadInFlight.add(userId);
    this.fetchUserCompanies(userId);
  }

  public upsertLocalData(dto: CompanyDTO): Signal<Company> {
    return dto ? this.companies.upsert(this.companyFactory.produce(dto)) : signal(undefined);
  }

  protected fetchUserCompanies(userId: string): void {
    this.httpClient.get<CompanyDTO[]>(environment.APIEndPoint + `users/${userId}/companies`).subscribe({
      next: (companies: CompanyDTO[]) => {
        const ids = companies.map(dto => dto.id);
        if (companies?.length) {
          this.companies.upsertMany(companies.map(dto => this.companyFactory.produce(dto)));
        }
        this.userCompanies.upsert({
          id: userId,
          ids: ids
        });
        this.userCompaniesLoadInFlight.delete(userId);
      },
      error: () => {
        this.userCompanies.upsert({
          id: userId,
          ids: []
        });
        this.userCompaniesLoadInFlight.delete(userId);
      }
    });
  }
}
